import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logout, setupAdminPassword, verifyAdminPassword } from "../../api/auth.api";
import { setAdminUnlock } from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../ui/Button";

// Same as ADMIN_PASSWORD_MIN_LENGTH in @titancloud/shared, which the web build can only import types from
const ADMIN_PASSWORD_MIN_LENGTH = 12;

const INPUT_CLASSES =
  "w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none";

/**
 * Shown instead of the app to an ADMIN session that has not entered the admin password.
 * The server blocks every other API route for that session until it does.
 */
export default function AdminPasswordGate({ mode }: { mode: "setup" | "verify" }) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === "setup") {
      if (password.length < ADMIN_PASSWORD_MIN_LENGTH) {
        setError(`Use at least ${ADMIN_PASSWORD_MIN_LENGTH} characters.`);
        return;
      }
      if (password !== confirm) {
        setError("The passwords do not match.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const { adminPasswordStatus, unlockToken } =
        mode === "setup" ? await setupAdminPassword(password) : await verifyAdminPassword(password);
      setAdminUnlock(unlockToken);
      setPassword("");
      setConfirm("");
      if (user) {
        const updated = { ...user, adminPasswordStatus };
        setUser(updated);
        qc.setQueryData(["auth", "me"], updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    qc.clear();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-950 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-600">
            <svg className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>

        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">
            {mode === "setup" ? "Create your admin password" : "Enter your admin password"}
          </h1>
          <p className="mt-2 text-sm text-gray-400">
            {mode === "setup"
              ? "This account is the TitanCloud admin. From now on, every new tab will ask for this password after Google sign-in."
              : `Signed in as ${user?.email ?? "admin"}. Each tab needs the admin password, and asks again after closing or after 24 hours.`}
          </p>
        </div>

        {mode === "setup" && (
          <p className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-3 py-2.5 text-xs text-amber-200">
            There is no way to reset this password from the app, by email or otherwise. Keep it
            somewhere safe.
          </p>
        )}

        <div className="space-y-3">
          {/* Lets password managers file this under the right account */}
          <input type="email" name="username" autoComplete="username" value={user?.email ?? ""} readOnly hidden />
          <input
            type="password"
            autoComplete={mode === "setup" ? "new-password" : "current-password"}
            placeholder="Admin password"
            aria-label="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={INPUT_CLASSES}
            autoFocus
            required
          />
          {mode === "setup" && (
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Confirm password"
              aria-label="Confirm password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={INPUT_CLASSES}
              required
            />
          )}
          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}
        </div>

        <Button type="submit" size="lg" loading={submitting} className="w-full">
          {mode === "setup" ? "Set password" : "Unlock"}
        </Button>

        <button
          type="button"
          onClick={handleLogout}
          className="block w-full text-center text-sm text-gray-500 hover:text-gray-300"
        >
          Sign out
        </button>
      </form>
    </div>
  );
}
